<template>
  <div class="automation-configure-campaign-settings-component" v-bind:class="{loaded, loading}">
    <div v-if="!loaded">
      <a-form-model-item label="Versturen als gebruiker">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; height: 32px;"></Skeleton>
      </a-form-model-item>
      <a-form-model-item label="E-mailaccount">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; height: 32px;"></Skeleton>
      </a-form-model-item>
      <a-form-model-item label="E-mailtemplate">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; height: 32px;"></Skeleton>
      </a-form-model-item>
      <a-form-model-item label="Opmaak (HTML-template)">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; height: 32px;"></Skeleton>
      </a-form-model-item>
    </div>
    <div v-else>
      <a-form-model-item label="Versturen als gebruiker" v-bind:required="true" :has-feedback="true"
                         :validate-status="hasValidationErrors('email_user_id')"
                         :help="getValidationMessage('email_user_id')">
        <users-select class="campaign-input-users mt4" style="width: 100%;" :disabled="loading"
                      :multiple="false"
                      v-bind:value="userId"
                      @change="onUserChange"/>
      </a-form-model-item>

      <a-form-model-item label="E-mailaccount" v-bind:required="true" :has-feedback="true"
                         :validate-status="hasValidationErrors('email_account_id')"
                         :help="getValidationMessage('email_account_id')">
        <email-accounts-select class="campaign-input-users mt4" style="width: 100%;" :disabled="loading"
                               :multiple="false"
                               v-bind:value="emailAccountId"
                               @change="onEmailAccountChange"/>
      </a-form-model-item>

      <a-form-model-item label="E-mailtemplate" v-bind:required="true" :has-feedback="true"
                         :validate-status="hasValidationErrors('email_template_id')"
                         :help="getValidationMessage('email_template_id')">
        <email-templates-select class="campaign-input-email-template mt4" style="width: 100%;"
                                :disabled="loading"
                                :multiple="false"
                                v-bind:value="emailTemplateId"
                                @change="onEmailTemplateChange"/>
      </a-form-model-item>

      <a-form-model-item label="Opmaak (HTML-template)">
        <select-html-email-template-component
            class="campaign-input-html-email-template-component mt4"
            :disabled="loading"
            v-bind:value="emailHtmlTemplateId"
            @change="onEmailHtmlTemplateChange"/>
      </a-form-model-item>

      <br/>

      <p>
        <InformationOutlineIcon/> Alleen klanten met een geldig e-mail adres ontvangen de e-mail.
      </p>

      <br/>

      <p>
        <a-switch v-model="verify" class="mr-md"/>
        Wil je de verzendlijst controleren voor het daadwerkelijk versturen?
        <br/>
        <small class="mt-sm" style="display: inline-block;">
          <i>Als je deze optie aanvinkt, dien je zelf het akkoord te geven op uitgaande
            e-mails.</i>
        </small>
      </p>

      <p v-if="false">
        <a-switch v-bind:value="sendEmailAtOfficeHours" v-on:change="onSendEmailAtOfficeHoursChange" class="mr-md"/>
        Binnen kantoortijden versturen
      </p>

    </div>
  </div>
</template>

<script>
import {Skeleton} from 'vue-loading-skeleton'
import automationStore from '../store'
import htmlEmailTemplateStore from '~/modules/html_email_template/store'
import emailAccountStore from '~/modules/email_account/store'
import {mapActions, mapGetters} from "vuex";

export default {
  name: 'AutomationConfigureEmailSettingsComponent',
  components: {
    Skeleton
  },
  props: {
    value: {
      type: Object,
      required: false,
      default: () => {
        return {};
      },
    },
  },
  data () {
    return {
      form: {},
      loaded: false,
      loading: false,
      template: null,
    }
  },
  mounted () {
    const AUTOMATION_STORE_KEY = '$_automation'
    // eslint-disable-next-line no-underscore-dangle
    if (!(AUTOMATION_STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(AUTOMATION_STORE_KEY, automationStore)
    }

    const HTML_EMAIL_TEMPLATE_STORE_KEY = '$_html_email_template'
    // eslint-disable-next-line no-underscore-dangle
    if (!(HTML_EMAIL_TEMPLATE_STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(HTML_EMAIL_TEMPLATE_STORE_KEY, htmlEmailTemplateStore)
    }

    const EMAIL_ACCOUNT_STORE_KEY = '$_email_account'
    // eslint-disable-next-line no-underscore-dangle
    if (!(EMAIL_ACCOUNT_STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(EMAIL_ACCOUNT_STORE_KEY, emailAccountStore)
    }

    Promise.all([
      this.fetchUsers(),
      this.fetchEmailAccounts(),
      this.fetchHtmlEmailTemplates(),
    ])
      .then()
      .catch()
      .finally(() => {
        this.loading = false
        this.loaded = true
      })
  },
  computed: {
    ...mapGetters('$_html_email_template', [
      'hasHtmlEmailTemplates',
    ]),
    ...mapGetters('$_form_page', [
      'errorsObject',
    ]),
    userId: {
      get () {
        return this.value.email_user_id || [];
      }
    },
    verify: {
      get () {
        return this.value.needs_verification || false;
      },
      set(value) {
        this.$emit('input', Object.assign({}, this.value, { needs_verification: value }))
      }
    },
    emailAccountId: {
      get () {
        return this.value.email_account_id || [];
      }
    },
    emailTemplateId: {
      get () {
        return this.value.email_template_id || [];
      }
    },
    emailHtmlTemplateId: {
      get () {
        return this.value.html_email_template_id || [];
      }
    },
    sendEmailAtOfficeHours: {
      get () {
        return this.value.send_email_at_office_hours;
      }
    },
  },
  methods: {
    ...mapActions('$_automation', {
      fetchUsers: 'fetchUsers',
      fetchMe: 'fetchMe',
    }),
    ...mapActions('$_html_email_template', {
      fetchHtmlEmailTemplates: 'fetchHtmlEmailTemplates',
    }),
    ...mapActions('$_email_account', {
      fetchEmailAccounts: 'fetchEmailAccounts',
    }),
    onUserChange (value) {
      this.$emit('input', Object.assign({}, this.value, { email_user_id: value }))
      this.clearErrorsByName('email_user_id')
    },
    onEmailAccountChange (value) {
      this.$emit('input', Object.assign({}, this.value, { email_account_id: value }))
      this.clearErrorsByName('email_account_id')
    },
    onEmailTemplateChange (value) {
      this.$emit('input', Object.assign({}, this.value, { email_template_id: value }))
      this.clearErrorsByName('email_template_id')
    },
    onEmailHtmlTemplateChange (value) {
      this.$emit('input', Object.assign({}, this.value, { html_email_template_id: value }))
    },
    onSendEmailAtOfficeHoursChange (value) {
      this.$emit('input', Object.assign({}, this.value, { send_email_at_office_hours: value }))
    },
    hasValidationErrors (key) {
      if (this.errorsObject !== undefined) {
        return Object.hasOwn(this.errorsObject, key) ? 'error' : null;
      }
    },
    getValidationMessage (key) {
      if (this.errorsObject !== undefined) {
        return Object.hasOwn(this.errorsObject, key) ? this.errorsObject[key][0] : null;
      }
    },
    clearErrorsByName (key){
      if ('$_form_page' in this.$store._modules.root._children) {
        this.$store.commit('$_form_page/CLEAR_ERRORS_BY_NAME', key);
      }
    },
  },
}
</script>

<style scoped lang="scss">
.automation-configure-signal-settings-component {
  position: relative;

  .ant-select {
    width: 100% !important;
    display: block !important;
  }
}
</style>
